'use client';

import Link from 'next/link';
import { redirect, usePathname } from 'next/navigation';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { NamesSteps } from './auth';
import { ModalsList } from './breadcrumbModal';
import { isCryptoCurrency } from './currency';
import {
  checkCompleteAdditionalUserData,
  someGroupMember,
  USER_GROUP,
} from './user';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { checkIsCmsPromoAvailable } from '@/modules/promotions';
import { useCommonStore } from '@/store';

export function withAuth<T>(Component: FC<T>) {
  return function WithAuth(props: T) {
    const user = useCommonStore.use.user();
    const pendingUser = useCommonStore.use.pendingUser();

    useEffect(() => {
      if (user || pendingUser) return;
      redirect('/');
    }, [user, pendingUser]);

    if (!user) return <GeneralSkeleton />;

    // @ts-ignore
    return <Component {...props} />;
  };
}

export function withPublic<T>(Component: FC<T>) {
  return function WithAuth(props: T) {
    const isAuthenticated = useCommonStore.use.isAuthenticated();
    const pendingUser = useCommonStore.use.pendingUser();
    const pathname = usePathname();
    const [isProcessing, setProcessing] = useState(true);

    useEffect(() => {
      if (pendingUser) return;
      if (isAuthenticated) {
        redirect('/');
      } else {
        setProcessing(false);
      }
    }, [isAuthenticated, pendingUser, pathname]);

    // @ts-ignore
    return isProcessing ? null : <Component {...props} />;
  };
}

interface WithGuardProps {
  userGroups: USER_GROUP[];
  redirectUrl: string;
  checkIsHiddenPage?: () => boolean;
  showIfNotAuthenticated?: boolean;
}

export function withGuard<T extends object>(
  Component: FC<T>,
  options: WithGuardProps
) {
  return function WithGuard(props: T) {
    const { isAuthenticated, pendingUser, user, pendingStore } = useCommonStore(
      useShallow((state) => ({
        isAuthenticated: state.isAuthenticated,
        pendingUser: state.pendingUser,
        user: state.user,
        pendingStore: state.pendingStore,
      }))
    );

    const {
      showIfNotAuthenticated,
      redirectUrl,
      checkIsHiddenPage,
      userGroups,
    } = options;

    const hiddenPage = checkIsHiddenPage && checkIsHiddenPage();

    useEffect(() => {
      if (
        hiddenPage ||
        (!isAuthenticated && !showIfNotAuthenticated) ||
        (user && someGroupMember(userGroups)(user))
      ) {
        if (!pendingStore) redirect(redirectUrl);
      }
    }, [
      isAuthenticated,
      user,
      hiddenPage,
      redirectUrl,
      showIfNotAuthenticated,
      userGroups,
      pendingStore,
    ]);

    if (pendingUser && !isAuthenticated) return <GeneralSkeleton />;

    return <Component {...props} />;
  };
}

export function withRegistration<T>(Component: FC<T>) {
  return function WithRegistration(props: T) {
    const { pendingUser, isAuthenticated, user, currencies } = useCommonStore(
      useShallow((state) => ({
        pendingUser: state.pendingUser,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        currencies: state.currencies,
      }))
    );
    const pathname = usePathname();
    const [isProcessing, setProcessing] = useState(true);

    useEffect(() => {
      if (pendingUser || !pathname.includes(ModalsList.REGISTRATION)) return;

      const completeAdditionalUserData = checkCompleteAdditionalUserData(
        user,
        currencies,
        user?.state !== null
      );

      if (!user) {
        useCommonStore.setState({
          currentRegistrationStep: NamesSteps.PRIMARY_INFORMATION,
          isAdditionalUserDataForm: false,
        });
        setProcessing(false);
      } else if (
        user &&
        completeAdditionalUserData &&
        !isCryptoCurrency(currencies, user?.currency)
      ) {
        useCommonStore.setState({
          currentRegistrationStep: NamesSteps.PERSONAL_INFORMATION,
        });
        setProcessing(false);
      } else {
        redirect('/');
      }
    }, [isAuthenticated, pendingUser, pathname, user, currencies]);

    // @ts-ignore
    return isProcessing ? null : <Component {...props} />;
  };
}

interface LinkWrapperProps extends PropsWithChildren {
  withLink: boolean;
  href: string;
  rel?: string;
  onClick?: () => void;
}

export const LinkWrapper: FC<LinkWrapperProps> = ({
  withLink,
  href,
  children,
  ...props
}) =>
  withLink ? (
    <Link href={href} {...props}>
      {children}
    </Link>
  ) : (
    children
  );

interface WithRestrictionsProps {
  cb: () => boolean;
}

export function withRestrictions<T extends object>(
  Component: FC<T>,
  options: WithRestrictionsProps
) {
  return function WithR(props: T) {
    if (options.cb()) return <Component {...props} />;
    return null;
  };
}

interface withEstoniaLicenseProps {
  redirectUrl: string;
}

export function withEstoniaLicense<T>(
  Component: FC<T>,
  options: withEstoniaLicenseProps
) {
  return function WithEstoniaLicense(props: T) {
    const { isEstonia, isEstoniaLoading } = useEstonia();

    useEffect(() => {
      if (isEstonia && !isEstoniaLoading) redirect(options.redirectUrl);
    }, [isEstonia, isEstoniaLoading]);

    if (isEstoniaLoading) return <GeneralSkeleton />;

    // @ts-ignore
    return <Component {...props} />;
  };
}

interface WithPromoProps {
  redirectUrl: string;
  promoName: 'bonanzaPromo' | 'scratchGame' | 'fortuneWheel';
  restrictedGroups?: USER_GROUP[];
}

export function withPromo<T extends object>(
  Component: FC<T>,
  options: WithPromoProps
) {
  return function WithGuard(props: T) {
    const { pendingUser, user, cmsConfig, pendingCmsConfig } = useCommonStore(
      useShallow((state) => ({
        pendingUser: state.pendingUser,
        user: state.user,
        cmsConfig: state.cmsConfig,
        pendingCmsConfig: state.pendingCmsConfig,
      }))
    );

    const { navigate } = useNavigation();

    useEffect(() => {
      if (
        pendingUser ||
        pendingCmsConfig ||
        checkIsCmsPromoAvailable(
          cmsConfig?.[options.promoName]?.availableByDate,
          user,
          options.restrictedGroups
        )
      )
        return;

      navigate(options.redirectUrl);
    }, [cmsConfig, pendingUser, pendingCmsConfig, user, navigate]);

    if (pendingUser) return <GeneralSkeleton />;

    return <Component {...props} />;
  };
}
